import type { CrateItem, MissingCrateItem, OrderDetails } from '@/entities/order/model'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { fetchOrdersFromApi } from '@/entities/order/api'
import { orderKeys } from '@/entities/order/model/order.queryKeys'
import { isAuthenticated } from '@/shared/api/api.token'
import { connectWebSocketForRealtime, disconnectWebSocketForStatus } from '@/shared/api/api.websocket'
import { authPersistence } from '@/shared/lib/storage'
import { queryClient } from '@/shared/lib/tanstack-query'

interface RealtimeState {
  realtimeOrders: OrderDetails[]
  realtimeWaitTime: { waitTimeAverage: number, numberOfOrders: number } | null
  useRealtime: boolean
  isConnected: boolean
  lastRealtimeEventTime: string
  lastStateUpdateTime: string
  shouldConnect: boolean
  isInitialized: boolean
  isInitialSyncComplete: boolean
  invalidateQueriesCallback: (() => void) | null
  invalidateStateUpdateCallback: (() => void) | null
  invalidateWaitTimeCallback: (() => void) | null
}

interface RealtimeStore extends RealtimeState {
  initialise: () => void
  setShouldConnect: (shouldConnect: boolean) => void
  updateConnectionStatus: (isConnected: boolean, lastEventTime?: string) => void
  handleOrderUpdate: (order: OrderDetails) => void
  handleWaitTimeUpdate: (waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => void
  handleStateUpdate: () => void
  syncOrders: (useRealtimeAfter?: boolean) => Promise<void>
  reset: () => void
  setInvalidateQueriesCallback: (callback: () => void) => void
  setInvalidateStateUpdateCallback: (callback: () => void) => void
  setInvalidateWaitTimeCallback: (callback: () => void) => void
}

const initialState: RealtimeState = {
  realtimeOrders: [],
  realtimeWaitTime: null,
  useRealtime: false,
  isConnected: false,
  lastRealtimeEventTime: '',
  lastStateUpdateTime: '',
  shouldConnect: false,
  isInitialized: false,
  isInitialSyncComplete: false,
  invalidateQueriesCallback: null,
  invalidateStateUpdateCallback: null,
  invalidateWaitTimeCallback: null,
}

/**
 * Union two crateScan snapshots for the same order. Scans are append-only while an order is in
 * its scan window, so neither side may drop what the other holds: this device persists a scan
 * locally the moment it happens, but the server only echoes it back once the zscans call lands.
 * Without this, a websocket payload arriving in that window would wipe the pending local scan.
 * The incoming (server) copy wins on collisions; the display layer filters resolved missing
 * crates by scanned-vs-expected counts.
 */
function mergeCrateScan(
  existing: OrderDetails['crateScan'],
  incoming: OrderDetails['crateScan'],
): OrderDetails['crateScan'] {
  if (!incoming) {
    return existing
  }
  if (!existing) {
    return incoming
  }

  const scannedByBarcode = new Map<string, CrateItem>()
  for (const item of [...existing.scannedBarcodes, ...incoming.scannedBarcodes]) {
    scannedByBarcode.set(item.barcode, item)
  }

  const missingByCrateType = new Map<string, MissingCrateItem>()
  for (const item of [...existing.missingCrates, ...incoming.missingCrates]) {
    missingByCrateType.set(item.crateType, item)
  }

  return {
    ...existing,
    ...incoming,
    scannedBarcodes: Array.from(scannedByBarcode.values()),
    missingCrates: Array.from(missingByCrateType.values()),
  }
}

/**
 * Update an order in the array. Adds new orders or updates existing ones if the new order
 * has a more recent lastUpdateTime. Returns a new array to maintain immutability.
 */
function updateOrderInArray(orders: OrderDetails[], newOrder: OrderDetails): OrderDetails[] {
  const newOrders = [...orders]
  const existingOrderIndex = newOrders.findIndex(
    existingOrder => existingOrder.orderId === newOrder.orderId,
  )

  if (existingOrderIndex === -1) {
    newOrders.push(newOrder)
  } else if (newOrders[existingOrderIndex].lastUpdateTime < newOrder.lastUpdateTime) {
    // Merge: preserve fields the WS event omits (e.g. customEventMetaData, areaCodeCrateTypeMapping)
    // so a partial WS payload never overwrites complete data fetched via HTTP on initial sync.
    const existingOrder = newOrders[existingOrderIndex]
    const crateScan = mergeCrateScan(existingOrder.crateScan, newOrder.crateScan)
    const merged: OrderDetails = {
      ...existingOrder,
      ...newOrder,
      ...(crateScan ? { crateScan } : {}),
    }
    // ETA is a lifecycle field: the backend drops it when an order leaves OnTheWay (e.g. -> Arrived).
    // Merging must not resurrect the stale value, so drop it when the status changes or is Arrived.
    const leftOnTheWay = newOrder.status !== undefined
      && (newOrder.status !== existingOrder.status || newOrder.status === 'Arrived')
    if (leftOnTheWay) {
      if (newOrder.eta === undefined) {
        delete merged.eta
      }
      if (newOrder.etaDirection === undefined) {
        delete merged.etaDirection
      }
    }
    newOrders[existingOrderIndex] = merged
  }

  return newOrders
}

export const useRealtimeStore = create<RealtimeStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    /**
     * Initialize the realtime store
     * Sets up WebSocket connection monitoring
     */
    initialise: () => {
      if (get().isInitialized) {
        return
      }

      set({ isInitialized: true })
    },

    /**
     * Set whether realtime should be connected
     * Manages WebSocket connection lifecycle
     */
    setShouldConnect: (shouldConnect: boolean) => {
      const currentShouldConnect = get().shouldConnect

      if (shouldConnect === currentShouldConnect) {
        return
      }

      set({ shouldConnect })

      if (shouldConnect && !currentShouldConnect) {
        const { token, region } = authPersistence.getState()

        if (region && token && isAuthenticated()) {
          connectWebSocketForRealtime(
            region,
            token,
            get().handleOrderUpdate,
            get().handleStateUpdate,
            get().handleWaitTimeUpdate,
            () => get().syncOrders(true),
          )
        }
      } else if (!shouldConnect && currentShouldConnect) {
        disconnectWebSocketForStatus()
        set({ useRealtime: false })

        // Invalidate state update query to restart polling when switching from realtime to polling
        const { invalidateStateUpdateCallback } = get()
        if (invalidateStateUpdateCallback) {
          invalidateStateUpdateCallback()
        }
      }
    },

    /**
     * Update connection status from WebSocket monitor
     */
    updateConnectionStatus: (isConnected: boolean, lastEventTime?: string) => {
      set(state => ({
        isConnected,
        lastRealtimeEventTime: lastEventTime || state.lastRealtimeEventTime,
      }))
    },

    /**
     * Handle order updates from WebSocket
     */
    handleOrderUpdate: (order: OrderDetails) => {
      set((state) => {
        const newOrders = updateOrderInArray(state.realtimeOrders, order)

        return {
          realtimeOrders: newOrders,
          lastRealtimeEventTime: new Date().toISOString(),
        }
      })

      // Apply the identical update to the query cache. Polling is disabled while the websocket
      // is connected, so the cache would otherwise freeze at its last HTTP snapshot and anything
      // reading it - offline scan reconciliation, the order modal's unmount patch - would
      // resurrect that stale copy over live state (BD-8161). Sharing updateOrderInArray keeps
      // both stores on one deterministic path instead of two that can drift apart.
      // Left untouched when the cache is empty so the initial fetch still owns population.
      // Skipped in tutorial mode: the cache holds mock orders then, and a live order must not be
      // merged into them (the realtime store above keeps the real list for after the tutorial).
      if (!globalThis.window?.__tutorialMode) {
        queryClient.setQueryData<OrderDetails[]>(orderKeys.orders(), old => (
          old ? updateOrderInArray(old, order) : old
        ))
      }

      // Invalidate wait time queries if order contains wait time data
      if (order.waitTime !== undefined) {
        const { invalidateWaitTimeCallback } = get()
        if (invalidateWaitTimeCallback) {
          invalidateWaitTimeCallback()
        }
      }
    },

    /**
     * Handle wait time updates from WebSocket
     */
    handleWaitTimeUpdate: (waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => {
      set({
        realtimeWaitTime: waitTimeData,
        useRealtime: true,
        lastRealtimeEventTime: new Date().toISOString(),
      })
    },

    /**
     * Handle state updates from WebSocket
     * This will be used by components to trigger external callbacks
     */
    handleStateUpdate: () => {
      set({ lastStateUpdateTime: new Date().toISOString() })

      const { invalidateStateUpdateCallback } = get()
      if (invalidateStateUpdateCallback) {
        invalidateStateUpdateCallback()
      }
    },

    /**
     * Fetch orders via HTTP and update store. Used for both immediate sync on mount
     * (useRealtimeAfter=false) and WebSocket initial sync (useRealtimeAfter=true).
     * The useRealtimeAfter parameter controls whether to switch to realtime mode after sync.
     */
    syncOrders: async (useRealtimeAfter: boolean = false) => {
      if (get().isInitialSyncComplete && !useRealtimeAfter) {
        return
      }

      try {
        // Real API only: during the onboarding tutorial fetchOrders() returns mock orders, which
        // would be stored here and shown on the real dashboard after the tutorial ends.
        const freshOrders = await fetchOrdersFromApi()
        set({
          realtimeOrders: freshOrders,
          useRealtime: useRealtimeAfter,
          lastRealtimeEventTime: new Date().toISOString(),
          isInitialSyncComplete: true,
        })
      } catch (error) {
        console.error('[RealtimeStore] Order sync failed:', error)
        // Fallback: enable realtime for new updates even if sync fails
        if (useRealtimeAfter) {
          set({
            useRealtime: true,
            lastRealtimeEventTime: new Date().toISOString(),
          })
        }
        set({
          isInitialSyncComplete: true,
        })
      }
    },

    /**
     * Reset the realtime state
     */
    reset: () => {
      set(initialState)
    },

    /**
     * Set callback to invalidate queries when switching from realtime to polling
     */
    setInvalidateQueriesCallback: (callback: () => void) => {
      set({ invalidateQueriesCallback: callback })
    },

    /**
     * Set callback to invalidate state update query when STATE_UPDATE is received or when realtime is disabled
     */
    setInvalidateStateUpdateCallback: (callback: () => void) => {
      set({ invalidateStateUpdateCallback: callback })
    },

    /**
     * Set callback to invalidate wait time queries when orders with waitTime are received
     */
    setInvalidateWaitTimeCallback: (callback: () => void) => {
      set({ invalidateWaitTimeCallback: callback })
    },
  })),
)

/**
 * Service for managing realtime state outside of React components
 */
export const realtimeService = {
  initialize: () => useRealtimeStore.getState().initialise(),
  setShouldConnect: (shouldConnect: boolean) => useRealtimeStore.getState().setShouldConnect(shouldConnect),
  getState: () => useRealtimeStore.getState(),
  reset: () => useRealtimeStore.getState().reset(),
}

// Expose realtime store on window for e2e tests and debugging
if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
  ;(window as any).useRealtimeStore = useRealtimeStore
}
